// The replaced-fonts report, out of CustomizerScreen and ProcessOrderScreen (backfill PR 31):
// the text file a user downloads after the customizer replaced the inactive fonts of the
// design it loaded, and the one the admin downloads for an order's designs, both from the
// summary checkAndReplaceInactiveFonts (utils.js) returns (one design's for the customizer, a
// list of designs' for the order). Since chain 10 (Oct 2026) also the numbers the customizer's
// modal shows (its header and body held the loops of the two reports).
export const replacedFontsNumbers = (replacedFontsSummary) => {
  const replacedFontsSet = new Set();
  let deletedCount = 0;

  replacedFontsSummary.replacedKeycaps.forEach((keycap) => {
    keycap.replacedTexts.forEach((textItem) => {
      replacedFontsSet.add(textItem.originalFontFamily);
      if (textItem.wasDeleted) {
        deletedCount++;
      }
    });
  });

  return { replacedFonts: Array.from(replacedFontsSet), deletedCount };
}; // the fonts one design's summary replaces, each once in the order met, and how many of its texts went with them

export const designReplacedFontsReport = (replacedFontsSummary, t) => {
  // 2) Start building the report
  let reportContent = 'Replaced Fonts Report\n\n';
  reportContent += `${t('customizer-screen-design-name')} ${
    replacedFontsSummary.designName
  }\n`;
  reportContent += `${t('customizer-screen-design-id')} ${
    replacedFontsSummary.designId
  }\n\n`;

  // 3) Collect replaced fonts & count deletions
  const { replacedFonts, deletedCount } =
    replacedFontsNumbers(replacedFontsSummary);

  // 4) Summarize replaced fonts & deletions
  reportContent += `${t(
    'customizer-screen-inactive-fonts'
  )} ${replacedFonts.join(', ')}\n`;
  if (deletedCount > 0) {
    reportContent += `${t(
      'customizer-screen-removed-text-entries'
    )} ${deletedCount}\n`;
  }
  reportContent += '\n';

  // 5) Detailed keycap info
  replacedFontsSummary.replacedKeycaps.forEach((keycap, index) => {
    reportContent += `${t('customizer-screen-keycap')} ${index + 1} (${t(
      'customizer-screen-row'
    )} ${keycap.rowIndex}, ${t('customizer-screen-column')} ${
      keycap.colIndex
    }):\n`;

    keycap.replacedTexts.forEach((textItem) => {
      if (textItem.wasDeleted) {
        reportContent += `  - ${t('customizer-screen-report-content-1')} "${
          textItem.textValue
        }" ${t('customizer-screen-report-content-2')} "${
          textItem.originalFontFamily
        }")\n`;
      } else {
        reportContent += `  - ${t('customizer-screen-report-content-3')} "${
          textItem.originalFontFamily
        }" ${t('customizer-screen-report-content-4')} "${
          textItem.textValue
        }"\n`;
      }
    });

    reportContent += '\n';
  });

  return {
    content: reportContent,
    fileName: `${t('customizer-screen-replaced-fonts-report')}_${
      replacedFontsSummary.designId
    }.txt`,
  };
}; // the customizer's report of one design, in the user's language, with the file name it downloads as

export const orderReplacedFontsReport = (replacedFontsSummary, order) => {
  // Start building the report content
  let reportContent = 'Replaced Fonts Report\n';
  reportContent += `Order: ${order.orderId}\n\n`;

  replacedFontsSummary.forEach((designInfo, designIndex) => {
    const orderItem = order.orderItems.find(
      (i) => i.designId === designInfo.designId
    );

    // 1) Build a heading for this design
    reportContent += `================ DESIGN #${
      designIndex + 1
    } ================\n`;
    if (orderItem) {
      reportContent += `Item Name: ${orderItem.name}\n`;
    }
    reportContent += `Design Name: ${designInfo.designName}\n`;
    reportContent += `Design ID: ${designInfo.designId}\n\n`;

    // 2) Collect replaced fonts & count deletions
    const { replacedFonts, deletedCount } = replacedFontsNumbers(designInfo);

    // Summarize the fonts
    reportContent += `Inactive Fonts Replaced: ${replacedFonts.join(', ')}\n`;
    if (deletedCount > 0) {
      reportContent += `Number of text entries removed entirely: ${deletedCount}\n`;
    }
    reportContent += '\n';

    // 3) Detailed keycap info
    designInfo.replacedKeycaps.forEach((keycap, index) => {
      reportContent += `Keycap ${index + 1} (Row ${keycap.rowIndex}, Column ${
        keycap.colIndex
      }):\n`;

      keycap.replacedTexts.forEach((textItem) => {
        if (textItem.wasDeleted) {
          reportContent += `  - Deleted text entry "${textItem.textValue}" because the fallback font does not support at least one glyph in this text. (Original Font: "${textItem.originalFontFamily}")\n`;
        } else {
          reportContent += `  - Replaced "${textItem.originalFontFamily}" with fallback font for text: "${textItem.textValue}"\n`;
        }
      });
      reportContent += '\n';
    });

    reportContent += '\n'; // Extra spacing before next design
  });

  return {
    content: reportContent,
    fileName: `ReplacedFontsReport_OrderID_${order.orderId}.txt`,
  };
}; // the order processing's report over the order's designs, in English like the rest of that page, with the item's name from the order
